// src/api/useLandingHtml.js
import { useCallback, useEffect, useRef, useState } from "react";
import axiosInstance from "./axiosInstance";

export function useLandingHtml(opts) {
  const endpoint = (opts && opts.endpoint) || "/cms/landing-html";
  const autoInjectCss = (opts && typeof opts.autoInjectCss === "boolean")
    ? opts.autoInjectCss
    : true;

  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  const abortRef = useRef(null);

  const hash = (s) => {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
    return String(h);
  };

  const injectCss = useCallback((css) => {
    if (!autoInjectCss || !css || typeof document === "undefined") return;

    const id = "landing-db-css";
    let el = document.getElementById(id);
    const newHash = hash(css);

    if (!el) {
      el = document.createElement("style");
      el.id = id;
      el.setAttribute("data-hash", newHash);
      el.textContent = css;
      document.head.appendChild(el);
      return;
    }

    const currentHash = el.getAttribute("data-hash");
    if (currentHash !== newHash) {
      el.textContent = css;
      el.setAttribute("data-hash", newHash);
    }
  }, [autoInjectCss]);

  const fetchOnce = useCallback(async () => {
    // cancel any in-flight request
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    setError(null);

    try {
      const { data } = await axiosInstance.get(endpoint, { signal: ctrl.signal });
      setHtml((data && data.html) || "");
      injectCss((data && data.css) || "");
      setUpdatedAt((data && data.updatedAt) || null);
    } catch (e) {
      // Ignore axios cancellation errors
      const name = e && e.name;
      const code = e && e.code;
      if (name !== "CanceledError" && code !== "ERR_CANCELED") {
        setError(e);
      }
    } finally {
      setLoading(false);
    }
  }, [endpoint, injectCss]);

  useEffect(() => {
    fetchOnce();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchOnce]);

  // optional helper for saving from anywhere
  const save = useCallback(async (nextHtml, nextCss) => {
    await axiosInstance.put(endpoint, {
      html: nextHtml,
      css: nextCss != null ? nextCss : null,
      ifUnmodifiedSince: updatedAt,
    });
    await fetchOnce();
  }, [endpoint, updatedAt, fetchOnce]);

  return { html, loading, error, updatedAt, refresh: fetchOnce, save };
}
